/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import { Injectable } from '@angular/core';
import * as _ from 'underscore';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';

@Injectable({
  providedIn: 'root'
})
export class TabContextService {
  private tabContextCache = new Map<any, any>();

  constructor(private currentExamContext: CurrentExamContextService) {}

  saveState(requestedProcedureId, key, state) {
    let examStateCache = this.tabContextCache.get(requestedProcedureId);

    if (!examStateCache) {
      examStateCache = new Map<any, any>();
      this.tabContextCache.set(requestedProcedureId, examStateCache);
    }

    // Cache object does not insert undefined values so using null.
    examStateCache.set(key, _.isUndefined(state) ? null : state);
  }

  getState(requestedProcedureId, key) {
    const examStateCache = this.tabContextCache.get(requestedProcedureId);

    if (examStateCache) {
      return examStateCache.get(key);
    }
  }

  getAssociableExamsState(requestedProcedureId) {
    return this.getCacheDataByName(requestedProcedureId, 'associableExams');
  }

  removeState(requestedProcedureId, key) {
    const examStateCache = this.tabContextCache.get(requestedProcedureId);
    if (examStateCache) {
      examStateCache.delete(key);
    }
  }

  removeStateForExam(requestedProcedureId) {
    const examStateCache = this.tabContextCache.get(requestedProcedureId);

    if (examStateCache) {
      this.tabContextCache.delete(requestedProcedureId);
    }
  }

  saveAssociatedExamsInCache(id, exams) {
    this.saveState(id, 'associableExams', exams);
  }

  getAssociationListAccNumbers() {
    const cachedAssociableExams = this.getAssociableExamsState(this.currentExamContext.getContext());
    if (cachedAssociableExams) {
      const keys = _.keys(cachedAssociableExams);
      if (keys.length > 0) {
        const associationList = [];
        _.each(keys, key => {
          if (cachedAssociableExams[key].isAssociated === 'Y') {
            associationList.push(key);
          }
        });
        return associationList.join();
      }
    }
  }

  getAccNumber(workItemId, contextWorkItemId) {
    const cachedAssociableExams = this.getAssociableExamsState(contextWorkItemId);
    if (cachedAssociableExams) {
      for (const key in cachedAssociableExams) {
        if (cachedAssociableExams[key].requestedProcedureId === workItemId) {
          return key;
        }
      }
    }
    return '';
  }

  getAssociatedIds(id, examContext?) {
    const values = this.getCacheData(id);
    if (values && values.length > 0) {
      const associationList = [];
      if (examContext && examContext === 'preview') {
        _.each(values, exam => {
          if (exam.isAssociated === 'Y' && exam.state && !exam.state.isActivatedBySelf) {
            associationList.push(exam.requestedProcedureId);
          }
        });
      } else {
        _.each(values, exam => {
          if (exam.isAssociated === 'Y') {
            associationList.push(exam.requestedProcedureId);
          }
        });
      }
      if (associationList.length > 0) {
        return associationList;
      }
    }
  }

  getAssociatedExamList(id) {
    const associableExams = this.getAssociableExamsState(id);
    const associatedExamList = {};
    if (associableExams) {
      const keys = _.keys(associableExams);
      if (keys && keys.length > 0) {
        _.each(keys, key => {
          if (associableExams[key].isAssociated === 'Y') {
            associatedExamList[key] = {
              requestedProcedureId: associableExams[key].requestedProcedureId
            };
          }
        });
        return associatedExamList;
      }
    }
  }

  private getCacheData(id) {
    const cachedAssociableExams = this.getAssociableExamsState(id);
    if (cachedAssociableExams) {
      return _.values(cachedAssociableExams);
    }
  }

  private getCacheDataByName(id, cacheName) {
    const examStateCache = this.tabContextCache.get(id);
    if (examStateCache) {
      return examStateCache.get(cacheName);
    }
  }

  setAssociatedExams(id, exams) {
    this.saveState(id, 'associatedExams', exams);
  }

  getAssociatedExams(id) {
    return this.getCacheDataByName(id, 'associatedExams');
  }

  setReportChangeLogs(id, lifecycle) {
    this.saveState(id, 'reportChangeLogs', lifecycle);
  }

  getReportChangeLogs(id) {
    return this.getCacheDataByName(id, 'reportChangeLogs');
  }
}
